// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import HeaderClickable, { HeaderClickableStyle } from "@guestsupply/App/Components/Header/HeaderClickable";
import MiniMyAccount from "@guestsupply/App/Components/MiniMyAccount/MiniMyAccount";
import MiniSignIn from "@guestsupply/App/Components/MiniSignIn/MiniSignIn";
import { TippyPopupStyle } from "@guestsupply/App/Components/Tippy/Style";
import TippyPopup from "@guestsupply/App/Components/Tippy/TippyPopup";
import xngGaToolBarNavigationEvent from "@guestsupply/App/Handlers/GoogleAnalytics/ToolBarNavigation/ToolBarNavigationEvent";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import { xIsAuthenticated } from "@guestsupply/App/Store/Selectors/GeneralSelectors";
import { xHasRoles } from "@guestsupply/App/Store/Selectors/SessionSelectors";
import { MappedLink } from "@guestsupply/MobiusOverrides/Menu/Menu";
import { USER_ROLES } from "@guestsupply/XDL/constants";
import Account from "@guestsupply/XDL/Icons/Account";
import AccountFocused from "@guestsupply/XDL/Icons/AccountFocused";
import { addScrollbarGutter, removeScrollbarGutter } from "@guestsupply/XDL/Utilities";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFlyoutDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetFlyoutDrawerIsOpen";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import setSignInOverlayIsOpen from "@insite/client-framework/Store/Components/SignInOverlay/Handlers/SetSignInOverlayIsOpen";
import { getCurrentUserIsGuest, getIsPunchOutSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import setShowInventory from "@insite/client-framework/Store/Context/Handlers/SetShowInventory";
import setShowPricing from "@insite/client-framework/Store/Context/Handlers/SetShowPricing";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import {
    getPageLinkByNodeId,
    getPageLinkByPageType,
    LinkModel,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import setIsReorderConfirmationModalOpen from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetIsReorderConfirmationModalOpen";
import setNextAction from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetNextAction";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import FieldDefinition from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import User from "@insite/mobius/Icons/User";
import * as ariaAppHider from "@insite/mobius/Overlay/helpers/ariaAppHider";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";
/* eslint-disable spire/fenced-imports */
import { hideAll } from "tippy.js";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
    // includeAccountMenu = "includeAccountMenu",
    showPricingToggle = "showPricingToggle",
    showInventoryToggle = "showInventoryToggle",
    // XNG-Change
    orderManagementSectionTitle = "orderManagementSectionTitle",
    orderManagementLinks = "orderManagementLinks",
    accountManagementTitle = "accountManagementTitle",
    accountManagementLinks = "accountManagementLinks",
}

// XNG-Change
export const mapLinks = (state: ApplicationState, widgetLinks: LinkModel[]) => {
    const myAccountPage = getPageLinkByPageType(state, "MyAccountPage");
    const visibleMyAccountLinks = myAccountPage?.children?.filter(pageLink => !pageLink.excludeFromNavigation);
    const displayAutoDeliveryLink = xHasRoles(state, [
        USER_ROLES.ADMIN,
        USER_ROLES.BUYER2,
        USER_ROLES.BUYER3,
        USER_ROLES.PUNCHOUT,
    ]);
    const displayMGOptionsLink = xHasRoles(state, ["MGAdmin"]);

    return (
        widgetLinks
            ?.map(widgetLink => {
                if (widgetLink.fields.destination.type === "Page") {
                    const pageLink = getPageLinkByNodeId(state, widgetLink.fields.destination.value);
                    if (pageLink && visibleMyAccountLinks?.every(link => link.url !== pageLink.url)) {
                        return undefined;
                    }
                    if (pageLink && pageLink.type === "AutoDeliveryPage" && !displayAutoDeliveryLink) {
                        return undefined;
                    }
                    if (pageLink && pageLink.type === "ManagementGroupOptionsPage" && !displayMGOptionsLink) {
                        return undefined;
                    }
                    if (pageLink) {
                        return {
                            ...pageLink,
                            title: widgetLink.fields.overriddenTitle || pageLink?.title,
                            openInNewWindow: widgetLink.fields.openInNewWindow,
                        };
                    }
                } else if (widgetLink.fields.destination.type === "Url") {
                    const url = widgetLink.fields.destination.value;
                    const title = widgetLink.fields.overriddenTitle;

                    return { id: "", url, title };
                }
                return undefined;
            })
            ?.filter(pageLink =>
                (pageLink as any)?.parentId === myAccountPage?.id ? !(pageLink as any)?.excludeFromNavigation : true,
            ) || []
    );
};

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    // XNG-Changes divide to sections to match design
    const orderManagementLinks = mapLinks(state, ownProps.fields.orderManagementLinks);
    const accountManagementLinks = mapLinks(state, ownProps.fields.accountManagementLinks);
    const linkGroups = [
        { title: ownProps.fields.accountManagementTitle, links: accountManagementLinks },
        { title: ownProps.fields.orderManagementSectionTitle, links: orderManagementLinks },
    ];
    return {
        isSignInPage: getCurrentPage(state).type === "SignInPage",
        userName: state.context.session.userName,
        myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        currentUserIsGuest: getCurrentUserIsGuest(state),
        isPunchOutSession: getIsPunchOutSession(state),
        currentLocation: getLocation(state),
        displayChangeCustomerLink: state.context.session?.displayChangeCustomerLink || false,
        displayPricingAndInventory: state.context.session?.displayPricingAndInventory || false,
        showInventory: state.context.showInventory,
        showPricing: state.context.showPricing,
        isReorderConfirmationModalOpen: state.pages.vmiBins.isReorderConfirmationModalOpen,
        hasChangeLastQty:
            state.pages.vmiBins.selectVmiBinsChangedQty &&
            Object.keys(state.pages.vmiBins.selectVmiBinsChangedQty).length > 0,
        // XNG-Change
        isAuthenticated: xIsAuthenticated(state),
        isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
        linkGroups,
    };
};

const mapDispatchToProps = {
    signOut,
    cancelPunchOut,
    setSignInOverlayIsOpen,
    setShowInventory,
    setShowPricing,
    setIsReorderConfirmationModalOpen,
    setNextAction,
    // XNG-Change
    setFlyoutDrawerIsOpen,
    setNavDrawerIsOpen,
    // xng-change: add google event
    xngGaToolBarNavigationEvent,
};

export interface HeaderSignInStyles {
    signOutWrapper?: InjectableCss;
    titleClickable?: ClickablePresentationProps;
    titleIcon?: IconPresentationProps;
    titleTypography?: TypographyPresentationProps;
    signOutClickable?: ClickablePresentationProps;
    // XNG-Change
    tippyPopup?: TippyPopupStyle;
    headerClickable?: HeaderClickableStyle;
}

export const headerSignInStyles: HeaderSignInStyles = {
    signOutWrapper: {
        css: css`
            display: inline-flex;
            padding: 6px 0;
        `,
    },
    titleClickable: {
        css: css`
            margin-left: 10px;
        `,
    },
    titleIcon: {
        size: 22,
    },
    titleTypography: {
        css: css`
            margin-left: 10px;
            max-width: 150px;
        `,
        ellipsis: true,
        transform: "uppercase",
    },
    signOutClickable: {
        css: css`
            flex: 0 0 auto;
            margin-left: 16px;
            text-transform: uppercase;
        `,
    },
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: string;
        [fields.icon]: string;
        // [fields.includeAccountMenu]: boolean;
        [fields.showPricingToggle]: boolean;
        [fields.showInventoryToggle]: boolean;
        // XNG-Changes
        [fields.orderManagementSectionTitle]: string;
        [fields.orderManagementLinks]: LinkModel[];
        [fields.accountManagementTitle]: string;
        [fields.accountManagementLinks]: LinkModel[];
    };
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    ThemeProps<BaseTheme>;

let icon: React.ComponentType<IconPresentationProps>;

export const HeaderSignIn = ({
    userName,
    isSignInPage,
    currentUserIsGuest,
    signOut,
    cancelPunchOut,
    fields,
    history,
    myAccountPageLink,
    signInUrl,
    isPunchOutSession,
    currentLocation,
    displayChangeCustomerLink,
    displayPricingAndInventory,
    showInventory,
    showPricing,
    setSignInOverlayIsOpen,
    setShowInventory,
    setShowPricing,
    theme,
    isReorderConfirmationModalOpen,
    hasChangeLastQty,
    setIsReorderConfirmationModalOpen,
    setNextAction,
    // XNG-Change
    isAuthenticated,
    isTabletScreen,
    setNavDrawerIsOpen,
    setFlyoutDrawerIsOpen,
    linkGroups,
    // xng-change: add google event
    xngGaToolBarNavigationEvent,
}: Props) => {
    // XNG-Changes
    const [isPopupShown, setIsPopupShown] = useState(false);

    const styles = useMergeStyles("headerSignIn", headerSignInStyles);
    useEffect(() => {
        window.onpageshow = function (event) {
            if (event.persisted) {
                window.location.reload();
            }
        };
    }, [userName]);

    if (!myAccountPageLink) {
        return null;
    }

    const { visibilityState, showInventoryToggle, showPricingToggle } = fields;
    const showIcon = visibilityState === "both" || visibilityState === "icon";
    const showLabel = visibilityState === "both" || visibilityState === "label";

    if (fields.icon === "User") {
        icon = Account;
    }

    const currentMode = getCookie(NavigationModeCookieName);
    let menuItems = (myAccountPageLink?.children?.filter(
        pageLink => !(pageLink.type === "ChangeCustomerPage" && !displayChangeCustomerLink),
    ) ?? []) as MappedLink[];

    const getMenuItem = (title: "Inventory" | "Pricing"): MappedLink => {
        return {
            title: title === "Inventory" ? translate("Inventory") : translate("Pricing"),
            iconProps: {
                src: (title === "Inventory" && showInventory) || (title === "Pricing" && showPricing) ? EyeOff : Eye,
                size: 22,
                css: css`
                    margin-left: 20px;
                    margin-right: -12px;
                `,
            },
            clickableProps: {
                css: css`
                    display: flex;
                    text-transform: uppercase;
                `,
                onClick: () => {
                    title === "Inventory"
                        ? setShowInventory({ showInventory: !showInventory })
                        : setShowPricing({ showPricing: !showPricing });
                },
            },
        };
    };

    if (!currentMode || currentMode === "Storefront") {
        menuItems = menuItems?.filter(pageLink => pageLink.type !== "VmiUsersPage");

        showInventoryToggle && displayPricingAndInventory && menuItems.push(getMenuItem("Inventory"));
        showPricingToggle && displayPricingAndInventory && menuItems.push(getMenuItem("Pricing"));
    }

    const onSignInHandler = (e: React.MouseEvent) => {
        e.preventDefault();

        setTimeout(() => {
            // XNG-CHANGES : To make sure all tippy are hidden and scrollbar is present
            hideAll();
            removeScrollbarGutter();
            ariaAppHider.show(document.body);
        }, 500);

        if ((!userName || currentUserIsGuest) && !isSignInPage) {
            if (!signInUrl) {
                logger.warn("No url was found for SignInPage, defaulting to /SignIn");
                history.push("/MyAccount/SignIn");
            } else {
                const returnUrl = currentLocation.pathname + currentLocation.search;
                if (returnUrl === "/") {
                    history.push(`${signInUrl}`);
                } else {
                    history.push(`${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}&clientRedirect=true`);
                }
            }
            return;
        }
        if (isAuthenticated) {
            history.push(myAccountPageLink?.url || "/MyAccount/SignIn");
        }
    };

    // XNG-Change
    const closeNavigationDrawer = () => {
        setFlyoutDrawerIsOpen({ isOpen: false });
        setTimeout(() => setFlyoutDrawerIsOpen({ isOpen: undefined }), 300);
        setNavDrawerIsOpen({ navDrawerIsOpen: false });
        setTimeout(() => setNavDrawerIsOpen({ navDrawerIsOpen: undefined }), 300);
    };

    const onSignOutHandler = () => {
        if (!isReorderConfirmationModalOpen && hasChangeLastQty) {
            setIsReorderConfirmationModalOpen({ isReorderConfirmationModalOpen: true });
            setNextAction({ nextAction: signOutFunc });
        } else {
            signOutFunc();
        }
    };

    const signOutFunc = () => {
        isPunchOutSession ? cancelPunchOut() : signOut();
    };

    let signInStatusText = translate("Sign In / Register");
    if (userName && !currentUserIsGuest) {
        signInStatusText = translate("My Account");
    }

    const headerClickableComponent = (
        <HeaderClickable
            isOpen={isPopupShown}
            onClick={onSignInHandler}
            iconProps={showIcon ? { src: AccountFocused } : undefined}
            data-test-selector="header_signIn"
            title={showLabel ? signInStatusText : ""}
            extendedStyles={styles.headerClickable}
        />
    );

    // Hide SignInOverlay + MiniAccountOverlay On tablet screens
    if (isTabletScreen) {
        return headerClickableComponent;
    }

    return (
        <>
            {/* {includeAccountMenu && userName && !currentUserIsGuest && (
                <Menu
                    descriptionId="accountMenu"
                    menuItems={menuItems}
                    displayChangeCustomerLink={displayChangeCustomerLink}
                    maxDepth={1}
                    menuTrigger={
                        <Clickable {...styles.titleClickable} data-test-selector="header_signIn">
                            {showIcon && <Icon {...styles.titleIcon} src={icon} title={userName} />}
                            {showLabel && (
                                <Typography {...styles.titleTypography} data-test-selector="header_userName">
                                    {userName}
                                </Typography>
                            )}
                        </Clickable>
                    }
                    cssOverrides={{
                        wrapper: css`
                            flex: 0 1 auto;
                            min-width: 0;
                        `,
                    }}
                    data-test-selector="header_accountMenu"
                />
            )} */}
            <TippyPopup
                extendedStyles={styles.tippyPopup}
                content={
                    isAuthenticated ? (
                        <MiniMyAccount linkGroups={linkGroups} onSignOutHandler={onSignOutHandler} />
                    ) : (
                        <MiniSignIn />
                    )
                }
                arrow={!isTabletScreen}
                offset={[12, 12]}
                maxWidth={isAuthenticated ? 430 : 343}
                placement="bottom-end"
                type="menu"
                showCloseButton={false}
                onTriggerPopup={isOpen => {
                    setIsPopupShown(isOpen);
                    // xng-change: add google event
                    xngGaToolBarNavigationEvent({
                        clickText: translate("My Account"),
                    });
                }}
                appendTo="parent"
                onShow={() => {
                    closeNavigationDrawer();
                }}
                popperOptions={{
                    strategy: "absolute",
                    modifiers: [
                        {
                            name: "preventOverflow",
                            options: {
                                padding: 0,
                            },
                        },
                        {
                            name: "computeStyles",
                            options: {
                                adaptive: true,
                                roundOffsets: ({ x, y }: any) => ({
                                    x,
                                    y,
                                }),
                            },
                        },
                    ],
                }}
            >
                {/* XNG-Change: use HeaderClickable component */}
                {headerClickableComponent}
            </TippyPopup>
        </>
    );
};

// XNG-Changes
export const linksFieldDefinitions = [
    {
        name: fields.accountManagementTitle,
        fieldType: "Contextual",
        displayName: "Title",
        editorTemplate: "TextField",
        defaultValue: "Account Management",
    },
    {
        displayName: "Links",
        name: fields.accountManagementLinks,
        editorTemplate: "ListField",
        fieldType: "Contextual",
        getDisplay: (item: HasFields, state: any) => {
            const { type, value } = item.fields.destination;
            const overriddenTitle = item.fields.overriddenTitle;
            if (type === "Page") {
                const link = getPageLinkByNodeId(state, value);
                return overriddenTitle || link?.title;
            }
            return overriddenTitle;
        },
        defaultValue: [],
        fieldDefinitions: [
            {
                name: "destination",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                isRequired: true,
                allowCategories: (item: HasFields) => false,
                allowUrls: (item: HasFields) => true,
            },
            {
                name: "overriddenTitle",
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                name: "openInNewWindow",
                displayName: "Open in New Window",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
    {
        name: fields.orderManagementSectionTitle,
        fieldType: "Contextual",
        displayName: "Title",
        editorTemplate: "TextField",
        defaultValue: "Product & Order Management",
    },
    {
        displayName: "Links",
        name: fields.orderManagementLinks,
        editorTemplate: "ListField",
        fieldType: "Contextual",
        getDisplay: (item: HasFields, state: any) => {
            const { type, value } = item.fields.destination;
            const overriddenTitle = item.fields.overriddenTitle;
            if (type === "Page") {
                const link = getPageLinkByNodeId(state, value);
                return overriddenTitle || link?.title;
            }
            return overriddenTitle;
        },
        defaultValue: [],
        fieldDefinitions: [
            {
                name: "destination",
                editorTemplate: "LinkField",
                defaultValue: {
                    value: "",
                    type: "Page",
                },
                isRequired: true,
                allowCategories: (item: HasFields) => false,
                allowUrls: (item: HasFields) => true,
            },
            {
                name: "overriddenTitle",
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                name: "openInNewWindow",
                displayName: "Open in New Window",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
];

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(HeaderSignIn))),
    definition: {
        displayName: "Header Sign In",
        icon: "arrow-right-to-bracket",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "SignIn Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "User",
                        value: "User",
                    },
                ],
                defaultValue: "User",
                fieldType: "General",
                sortOrder: 2,
            },
            // {
            //     name: fields.includeAccountMenu,
            //     displayName: "Include My Account Menu",
            //     editorTemplate: "CheckboxField",
            //     defaultValue: true,
            //     fieldType: "General",
            //     sortOrder: 3,
            // },
            {
                name: fields.showPricingToggle,
                displayName: "Show/Hide Pricing Toggle",
                editorTemplate: "CheckboxField",
                isVisible: widget => widget.fields.includeAccountMenu,
                defaultValue: false,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showInventoryToggle,
                displayName: "Show/Hide Inventory Toggle",
                editorTemplate: "CheckboxField",
                isVisible: widget => widget.fields.includeAccountMenu,
                defaultValue: false,
                fieldType: "General",
                sortOrder: 5,
            },
            // XNG-Change
            ...linksFieldDefinitions,
        ] as FieldDefinition[],
        group: "Common",
    },
};

export default widgetModule;
